<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  livecamData: {
    type: Object,
    default: () => ({})
  }
})

let clearId = null
const route = useRoute()
const sliderRef = ref(null)
const isPlaying = ref(false)
const range = ref('tenmin')
const cameraId = route.params.id
const interval = computed(() => (range.value === 'tenmin' ? 1000 : 100))
const imgList = computed(() => {
  if (range.value === 'tenmin') {
    return props.livecamData.tenmin
  } else if (range.value === 'onehour') {
    return props.livecamData.onehour
  }
  return {}
})
const sliderValue = ref(imgList.value.length - 1)
const imgUrl = computed(() => imgList.value[sliderValue.value].file)
const selectedTime = computed(() => (dayjs.unix(imgList.value[sliderValue.value].time).tz('Asia/Tokyo').format('M月D日(ddd) HH:mm')))
const anime = () => {
  if (sliderValue.value < imgList.value.length - 1) {
    sliderValue.value++
  } else {
    sliderValue.value = 0
  }
  if (sliderValue.value === imgList.value.length - 1) {
    clearId = setTimeout(anime, 2000)
  } else {
    clearId = setTimeout(anime, interval.value)
  }
}
const onClickPlay = () => {
  isPlaying.value = true
  clearTimeout(clearId)
  clearId = setTimeout(anime, interval.value)
}
const onClickStop = () => {
  isPlaying.value = false
  clearTimeout(clearId)
}
const updateSliderBackground = () => {
  if (sliderRef.value) {
    const value = (sliderValue.value / sliderRef.value.max) * 100
    sliderRef.value.style.background = `linear-gradient(to right, #3569c0 0%, #3569c0 ${value}%, #8793ae ${value}%, #8793ae 100%)`
  }
}

watch(imgList, () => {
  sliderValue.value = imgList.value.length - 1
  onClickStop()
  nextTick(() => {
    if (sliderRef.value) {
      sliderRef.value.value = sliderValue.value
      updateSliderBackground()
    }
  })
})

watch(sliderValue, () => {
  updateSliderBackground()
})

onMounted(() => {
  if (sliderRef.value) {
    updateSliderBackground()
    sliderRef.value.addEventListener('input', updateSliderBackground)
  }
})

onUnmounted(() => {
  if (sliderRef.value) {
    sliderRef.value.removeEventListener('input', updateSliderBackground)
  }
})
</script>

<template>
  <section class="livecamContent">
    <div class="head">
      <CommonPageTitle :title="`${livecamData.name}のライブカメラ`" />
    </div>
    <div class="block">
      <div class="image">
        <img
          v-if="!livecamData.old"
          :src="imgUrl"
          alt=""
        >
        <img
          v-else-if="livecamData.old"
          src="https://gvs.weathernews.jp/onebox/img/livecam/noimage.webp"
          alt=""
        >
      </div>
      <div class="inner">
        <div class="toggle">
          <p
            class="item"
            :data-selected="range === 'tenmin'"
            @click="range = 'tenmin'"
          >
            10分間
          </p>
          <p
            class="item"
            :data-selected="range === 'onehour'"
            @click="range = 'onehour'"
          >
            24時間
          </p>
        </div>
        <div class="content">
          <button
            v-show="!isPlaying"
            data-button="play"
            @click="onClickPlay"
          />
          <button
            v-show="isPlaying"
            data-button="stop"
            @click="onClickStop"
          />
          <div class="range">
            <input
              ref="sliderRef"
              v-model="sliderValue"
              type="range"
              min="0"
              :max="imgList.length - 1"
              step="1"
              @input="onSliderInput"
            >
          </div>
          <p class="date">
            {{ selectedTime }}
          </p>
        </div>
      </div>
      <div
        v-if="!Number(cameraId)"
        class="annotation"
      >
        <p>※ 本映像はMyライブカメラ企画によるものです</p>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.livecamContent {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    gap: 4px;
  }
  .block {
    .image {
      display: flex;
      justify-content: center;
      width: 100%;
      aspect-ratio: 16 / 9;
      background-color: #303030;
      img {
        height: 100%;
      }
    }
    .no-image {
      display: flex;
      justify-content: center;
      width: 100%;
      aspect-ratio: 16 / 9;
      background-color: transparent;
      img {
        height: 100%;
      }
    }
    .annotation {
      display: flex;
      justify-content: flex-end;
      p {
        color: #303030;
      }
    }
  }
  .inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 10px 15px;
    background-color: #eef1f8;
  }
  .toggle {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 3px;
    border-radius: 100vmax;
    background-color: #fff;
  }
  .item {
    flex: 1;
    padding: 4px 10px;
    text-align: center;
    color: #303030;
    font-size: 14px;
    line-height: 1em;
    border-radius: 100vmax;
    cursor: pointer;
    &[data-selected="true"] {
      background-color: #3569c0;
      color: #fff;
    }
  }
  .content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  button {
    width: 25px;
    height: 25px;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
    background-position: center;
    background-repeat: no-repeat;
    background-size: 12px;
    &[data-button="play"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_play.svg");
    }
    &[data-button="stop"] {
      background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_pause.svg");
    }
    @media (any-hover: hover) {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
  }
  .range {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
    input {
      position: relative;
      -webkit-appearance: none;
      appearance: none;
      cursor: pointer;
      outline: none;
      width: 100%;
      height: 3px;
      margin-inline: auto;
      background-color: #8793ae;
      border-radius: 100vmax;
      border: none;
      z-index: 2;
      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        background-color: #3569c0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        box-shadow: none;
      }
      &::-moz-range-thumb {
        background: #3569c0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        box-shadow: none;
        border: none;
      }
    }
  }
  .date {
    color: #303030;
    font-size: 14px;
    line-height: 1em;
  }
}
@media screen and (min-width: 830px) {
  .livecamContent {
    .toggle {
      width: auto;
    }
    .item {
      min-width: 185px;
    }
  }
}
</style>
